import React, { useState } from "react";
import { ConfirmModal } from "../../../../components/ui";

/**
 * SendToInspectorModal Component
 *
 * Modal for sending T&M report to inspector for signature.
 * Collects inspector email and optional copy email.
 * Form values are retained when the modal is closed (via retainDataOnClose; cleared on page leave/refresh).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Function} props.onConfirm - Callback to confirm send (receives { inspector_email, inspector_copy_email })
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {Object} props.report - TMReport instance
 * @returns {JSX.Element}
 */
const SendToInspectorModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  report,
}) => {
  const [inspectorName, setInspectorName] = useState("");
  const [inspectorEmail, setInspectorEmail] = useState("");
  const [copyEmail, setCopyEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showNextSteps, setShowNextSteps] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Validate optional comma-separated CC emails. Empty is valid.
   *
   * @param {string} emailString Raw CC field value.
   * @returns {boolean}
   */
  const validateMultipleEmails = (emailString) => {
    if (!emailString || emailString.trim() === "") {
      return true; // Empty is valid for optional field
    }

    const emails = emailString.split(",").map((e) => e.trim());
    return emails.every((email) => emailRegex.test(email));
  };

  /**
   * Validate inspector name/email/CC and call `onConfirm` with the send payload.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    // Validate inspector name
    if (!inspectorName || inspectorName.trim() === "") {
      setEmailError("Inspector name is required");
      return;
    }

    // Validate inspector email
    if (!inspectorEmail) {
      setEmailError("Inspector email is required");
      return;
    }

    if (!emailRegex.test(inspectorEmail)) {
      setEmailError("Please enter a valid inspector email address");
      return;
    }

    // Validate copy emails if provided (supports multiple comma-separated emails)
    if (copyEmail && !validateMultipleEmails(copyEmail)) {
      setEmailError(
        "Please enter valid CC email address(es). Multiple emails should be separated by commas."
      );
      return;
    }

    // Clear errors and proceed
    setEmailError("");
    onConfirm({
      inspector_name: inspectorName.trim(),
      inspector_email: inspectorEmail,
      inspector_copy_email: copyEmail || null,
    });
  };

  /**
   * Clear the inline error and close. Form values are retained
   * (`retainDataOnClose`).
   *
   * @returns {void}
   */
  const handleClose = () => {
    setEmailError("");
    onClose();
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Send Report to Inspector"
      buttonText={isSubmitting ? "Sending..." : "Save & Send"}
      isSubmitting={isSubmitting}
      retainDataOnClose
    >
      <div className="space-y-4">
        <p className="text-gray-700">
          This will save the report and send an email to the inspector with a
          unique review link.
        </p>

        {/* Inspector Name Input */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Inspector Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={inspectorName}
            onChange={(e) => {
              setInspectorName(e.target.value);
              setEmailError("");
            }}
            placeholder="John Smith"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            autoFocus
          />
        </div>

        {/* Inspector Email Input */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Inspector Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={inspectorEmail}
            onChange={(e) => {
              setInspectorEmail(e.target.value);
              setEmailError("");
            }}
            placeholder="inspector@example.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Copy Email Input (Optional) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            CC Emails (Optional)
          </label>
          <input
            type="text"
            value={copyEmail}
            onChange={(e) => {
              setCopyEmail(e.target.value);
              setEmailError("");
            }}
            placeholder="email1@example.com, email2@example.com"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Send a copy to multiple recipients (separate emails with commas)
          </p>
        </div>

        {/* Error Message */}
        {emailError && (
          <div className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-700">
            {emailError}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-300 rounded p-4">
          <button
            type="button"
            onClick={() => setShowNextSteps(!showNextSteps)}
            className="w-full flex justify-between items-center text-sm font-semibold text-blue-800 hover:text-blue-900"
          >
            <span>What happens next?</span>
            <span className="text-xs">
              {showNextSteps ? "▼ Hide" : "▶ Show"}
            </span>
          </button>

          {showNextSteps && (
            <ul className="text-sm text-left mx-[5%] text-blue-700 space-y-1 list-disc list-inside mt-3">
              <li>Report will be saved with current data</li>
              <li>
                Report will remain <strong>unlocked</strong> (editable) until
                the inspector signs
              </li>
              <li>Inspector will receive an email with a unique review link</li>
              <li>
                Inspector will see the most up to date version of the report
              </li>
              <li>Inspector can view the report PDF and add their signature</li>
              <li>
                Form will be <strong>locked</strong> once inspector signs (you
                can unlock it if needed)
              </li>
            </ul>
          )}
        </div>
      </div>
    </ConfirmModal>
  );
};

export default SendToInspectorModal;
